import React, { useState } from "react";
import { Button } from "../../components/template";

/**
 * Scoring:
 * A = 2 points
 * B = 1 point
 * C = 0 points
 */

const scoreMap = {
  0: 2,
  1: 1,
  2: 0,
};

const quizData = [
  {
    question: "When something stressful happens, my first inner response usually is…",
    options: [
      "I pause, take a breath, and try to understand what I’m feeling",
      "I react quickly — emotionally or verbally — and deal with it later",
      "I shut down, avoid it, or pretend it doesn’t bother me",
    ],
  },
  {
    question: "On most days, my emotional state feels…",
    options: [
      "Generally calm and steady",
      "Up and down depending on situations",
      "Heavy, numb, or emotionally drained",
    ],
  },
  {
    question: "When I feel low or overwhelmed, I usually…",
    options: [
      "Acknowledge it and take some form of emotional care",
      "Distract myself by staying busy",
      "Withdraw from people or isolate myself",
    ],
  },
  {
    question: "I feel emotionally supported and understood by people around me…",
    options: ["Most of the time", "Sometimes", "Rarely"],
  },
  {
    question: "After making a mistake, my inner dialogue sounds like…",
    options: [
      "“It’s okay, I’ll learn from this”",
      "“I should have done better”",
      "“I always mess things up”",
    ],
  },
  {
    question: "I am able to identify and name what I’m feeling (sad, anxious, irritated, etc.)",
    options: ["Easily", "With some effort", "I usually feel confused or blank"],
  },
  {
    question: "When emotions come up strongly, I usually…",
    options: [
      "Allow myself to feel and express them in a healthy way",
      "Hold them in until they pass",
      "Suppress or ignore them completely",
    ],
  },
  {
    question: "I allow myself to rest or slow down without feeling guilty…",
    options: [
      "Yes, most of the time",
      "Sometimes, but with discomfort",
      "Rarely — I feel guilty when I rest",
    ],
  },
  {
    question: "Over the past week, my emotional energy feels…",
    options: [
      "Balanced and manageable",
      "Drained on some days",
      "Constantly low or exhausted",
    ],
  },
  {
    question: "When someone gives me feedback or disagrees with me, I usually…",
    options: [
      "Stay open and grounded",
      "Feel defensive at first but reflect later",
      "Feel hurt, anxious, or shut down",
    ],
  },
];

/* -------------------- INTRO PAGE -------------------- */

const IntroPage = ({ onStart }) => (
  <div className="min-h-screen bg-[#FAF8F5] flex items-center justify-center px-6 py-10">
    <div
      className="
        relative
        overflow-hidden
        rounded-[34px]
        max-w-5xl
        w-full
        h-[620px]
        shadow-[0_30px_80px_rgba(20,10,0,.12)]
      "
    >
      <img
        src="/assets/knowyourself/quiz/emotional.jpg"
        alt="Emotional Wellbeing Quiz"
        className="
          w-full
          h-full
          object-cover
          brightness-[.84]
          saturate-[.9]
        "
      />

      <div
        className="
          absolute
          inset-0
          bg-gradient-to-t
          from-black/90
          via-black/35
          to-transparent
        "
      />

      <div className="absolute inset-x-0 bottom-0 p-6">
        <div
          className="
            rounded-[28px]
            bg-white/10
            backdrop-blur-2xl
            border
            border-white/10
            p-8
          "
        >
          <div
            className="
              inline-flex
              px-4
              py-2
              rounded-full
              bg-white/10
              backdrop-blur-xl
              border
              border-white/10
              text-white
              text-xs
              tracking-[.08em]
              mb-5
            "
          >
            SELF ASSESSMENT
          </div>

          <h1
            className="
              text-[38px]
              md:text-[56px]
              leading-[1.05]
              text-white
              mb-5
            "
          >
            Emotional Wellbeing
          </h1>

          <p
            className="
              text-white/75
              text-[15px]
              leading-8
              mb-4
            "
          >
            Think about how you've been feeling over the last 1–2 weeks.
          </p>

          <p
            className="
              text-white/75
              text-[15px]
              leading-8
              mb-8
            "
          >
            Choose the option that feels most true for you — not what you think should be true.
          </p>

          <Button
            variant="primary"
            text="Start Assessment"
            onClick={onStart}
          />
        </div>
      </div>
    </div>
  </div>
);

/* -------------------- QUIZ PAGE -------------------- */

const QuizPage = ({ onComplete }) => {
  const [current, setCurrent] = useState(0);
  const [answers, setAnswers] = useState(Array(quizData.length).fill(null));
  const [error, setError] = useState("");

  const handleSelect = (index) => {
    const updated = [...answers];
    updated[current] = index;
    setAnswers(updated);
    setError("");
  };

  const handleNext = () => {
    if (answers[current] === null) {
      setError("Please select an option to proceed.");
      return;
    }

    if (current < quizData.length - 1) {
      setCurrent(current + 1);
    } else {
      onComplete(answers);
    }
  };

  const handlePrev = () => {
    setError("");
    if (current > 0) setCurrent(current - 1);
  };

  const progress = ((current + 1) / quizData.length) * 100;

  return (
    <div className="flex flex-col items-center justify-center min-h-screen bg-light px-6 py-10">
      <div className="w-full max-w-2xl bg-white p-8 rounded-2xl shadow-lg text-center">
        <div className="mb-6">
          <p className="text-sm text-gray-600 mb-2">
            Question {current + 1} of {quizData.length}
          </p>
          <div className="w-full bg-gray-200 h-2 rounded-full">
            <div
              className="bg-primary h-2 rounded-full"
              style={{ width: `${progress}%` }}
            />
          </div>
        </div>

        <p className="text-[#578d48] font-bold text-xl mb-4">
          {quizData[current].question}
        </p>

        <div className="space-y-3">
          {quizData[current].options.map((opt, i) => (
            <label
              key={i}
              className={`flex items-center justify-center p-3 rounded-lg cursor-pointer transition
                ${
                  answers[current] === i
                    ? "bg-primary text-white"
                    : "bg-gray-100 hover:bg-gray-200"
                }`}
            >
              <input
                type="radio"
                checked={answers[current] === i}
                onChange={() => handleSelect(i)}
                className="hidden"
              />
              <span className="text-sm">{opt}</span>
            </label>
          ))}
        </div>

        {error && <p className="mt-4 text-red-500 text-sm">{error}</p>}
      </div>

      <div className="flex mt-6 space-x-4">
        {current > 0 && (
          <Button text="Previous" variant="whitish" onClick={handlePrev} />
        )}
        <Button
          text={current === quizData.length - 1 ? "Submit" : "Next"}
          variant="primary"
          onClick={handleNext}
        />
      </div>
    </div>
  );
};

/* -------------------- RESULT PAGE -------------------- */

const ResultPage = ({ answers, onRetake }) => {
  const totalScore = answers.reduce(
    (sum, ans) => sum + scoreMap[ans],
    0
  );

  let title = "";
  let resultText = "";

  if (totalScore >= 15) {
    title = "🌿 Emotionally Balanced & Resilient";

    resultText =
      "You are generally aware of your emotions and able to regulate them. You allow yourself to feel, reflect, and recover. Continue maintaining emotional hygiene, healthy boundaries, and self-awareness.";
  } else if (totalScore >= 8) {
    title = "🌱 Moderately Balanced";

    resultText =
      "You manage emotions reasonably well but may rely on distraction or suppression at times. Emotional ups and downs are common. Focus on emotional expression, rest, and regular self-check-ins.";
  } else {
    title = "🌧️ Emotional Fatigue or Overload";

    resultText =
      "You may be emotionally overwhelmed, numb, or burnt out. This is not weakness — it is a signal that your mind and body need care, support, and recovery.";
  }

  return (
    <div className="min-h-screen bg-[#FAF8F5] flex items-center justify-center px-6 py-10">
      <div
        className="
          relative
          overflow-hidden
          rounded-[34px]
          max-w-5xl
          w-full
          h-[620px]
          shadow-[0_30px_80px_rgba(20,10,0,.12)]
        "
      >
        <img
          src="/assets/knowyourself/quiz/result.jpg"
          alt="Assessment Result"
          className="
            w-full
            h-full
            object-cover
            brightness-[.84]
            saturate-[.9]
          "
        />

        <div
          className="
            absolute
            inset-0
            bg-gradient-to-t
            from-black/90
            via-black/35
            to-transparent
          "
        />

        <div className="absolute inset-x-0 bottom-0 p-6">
          <div
            className="
              rounded-[28px]
              bg-white/10
              backdrop-blur-2xl
              border
              border-white/10
              p-8
            "
          >
            <div
              className="
                inline-flex
                px-4
                py-2
                rounded-full
                bg-white/10
                backdrop-blur-xl
                border
                border-white/10
                text-white
                text-xs
                tracking-[.08em]
                mb-5
              "
            >
              YOUR RESULT
            </div>

            <h2
              className="
                text-[34px]
                md:text-[52px]
                leading-[1.05]
                text-white
                mb-4
              "
            >
              {title}
            </h2>

            <p
              className="
                text-white/75
                text-[15px]
                leading-8
                mb-4
              "
            >
              Score: {totalScore} / 20
            </p>

            <p
              className="
                text-white/75
                text-[15px]
                leading-8
                mb-8
                max-w-3xl
              "
            >
              {resultText}
            </p>

            <Button
              variant="primary"
              text="Retake Assessment"
              onClick={onRetake}
            />
          </div>
        </div>
      </div>
    </div>
  );
};

/* -------------------- MAIN COMPONENT -------------------- */

const EmotionalWellbeingQuiz = () => {
  const [started, setStarted] = useState(false);
  const [completed, setCompleted] = useState(false);
  const [answers, setAnswers] = useState(null);

  return (
    <>
      {!started ? (
        <IntroPage onStart={() => setStarted(true)} />
      ) : completed ? (
        <ResultPage
          answers={answers}
          onRetake={() => {
            setStarted(false);
            setCompleted(false);
            setAnswers(null);
          }}
        />
      ) : (
        <QuizPage
          onComplete={(ans) => {
            setAnswers(ans);
            setCompleted(true);
          }}
        />
      )}
    </>
  );
};

export default EmotionalWellbeingQuiz;
